import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/en/work-permit/type/")({
  loader: async () => {
    const { WP_CASE_TYPES } = await import("@/content/work-permit-corpus/wp-types");
    return { items: WP_CASE_TYPES.map((x) => ({ slug: x.slug, title: x.titleEn, desc: x.whoEn })) };
  },
  component: WpTypeHubEn,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Thai Work Permits — ${loaderData?.items.length ?? 0} Routes, Documents & Authorities`,
        description:
          "Work permit routes in Thailand: ordinary Thai company (Non-B), BOI / One Stop Service, foreign teachers, representative offices, SMART & LTR, restricted occupations, renewals and document legalization — with official sources.",
        path: "/en/work-permit/type",
        alternates: [{ lang: "th", path: "/work-permit/type" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildSpeakableSchema("/en/work-permit/type")),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Work Permits", url: `${site}/en/work-permit/type` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: (loaderData?.items ?? []).map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: t.title,
              url: `${site}/en/work-permit/type/${t.slug}`,
            })),
          }),
        },
      ],
    };
  },
});

function WpTypeHubEn() {
  const { items } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Work Permits</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Thai Work Permits — {items.length} Common Routes
        </h1>
        <p className="mb-8 max-w-3xl text-muted-foreground">
          Pick the route that matches your situation. Each page sets out the filing order, the documents,
          the deciding authority and the mistakes that get files sent back. Content is based on official
          notifications and agency websites; every application is decided at the officer's discretion.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((t: { slug: string; title: string; desc: string }) => (
            <a key={t.slug} href={`/en/work-permit/type/${t.slug}`} className="rounded-lg border bg-card p-5 transition hover:border-gold">
              <h2 className="mb-2 text-lg font-semibold">{t.title}</h2>
              <p className="text-sm text-muted-foreground">{t.desc}</p>
            </a>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-muted/30 p-5">
          <h2 className="mb-2 text-xl font-bold">Want your file checked before submission?</h2>
          <p className="text-sm text-muted-foreground">
            Ask our team by phone, LINE or email. We review your documents against the requirements for your route.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact our team</a>
        </section>
        <p className="mt-8 text-xs text-muted-foreground">
          Information as of August 2026. Please confirm current conditions with the Department of Employment or Immigration Bureau before acting.
        </p>
        <p className="mt-4 text-sm">
          Read the FAQ: <a href="/en/work-permit/faq" className="underline hover:text-gold">Work Permit FAQ</a>
        </p>
      </main>
      <SiteFooter />
    </>
  );
}
